import { formatString } from "@format-string/core";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon, Print as PrintIcon } from "@mui/icons-material";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useContextContrato } from "src/Contexts/Lancamentos/Contrato/ContratosContext";
import {
  IDependentesContrato,
  IListBorderoData,
  IValueContractInternal,
} from "src/Contexts/Lancamentos/Contrato/ContratosTypes";
import { useLayoutMainContext } from "src/Contexts/MainLayoutContext";
import { statusContract } from "src/Pages/TelasSistemas/3-Lancamentos/0 - Contratos/Components/FormCancelContrato/FormCancelContrato";
import { api } from "src/shared/setup/API/api";
import { handleFormatCep } from "src/shared/Utils/FormatCEP";
import { FormatDateBR } from "src/shared/Utils/FormatDateBR";
import { FormatMoney } from "src/shared/Utils/FormatMoney";
import { cpfOurCnpj } from "src/shared/Utils/MaskCpfCnpj";

type GradeItem = {
  produtor_nome?: string | null;
  produtor_code?: string | null;
  tipo_produtor?: string | null;
};

type ContratoCompletoData = IValueContractInternal & {
  taxa_value?: string | number | null;
  is_producer_receveid_installment?: boolean | "SIM" | "NAO" | null;
  gradeProdutor?: {
    corretor?: GradeItem | "" | null;
    supervisor?: GradeItem | "" | null;
  };
};

interface IContratoCompletoDialog {
  open: boolean;
  idContract: string;
  onClose: () => void;
}

const dateBr = (value?: string | null) => {
  if (!value) return "";
  const formatted = FormatDateBR(value);
  if (!formatted || formatted === "Invalid Date") return "";
  return formatted;
};

const money = (value?: string | number | null) => {
  if (value === undefined || value === null || value === "") return "0,00";
  return FormatMoney(String(value)) || "0,00";
};

const percent = (value?: string | null) => {
  if (!value) return "0,00";
  return String(value).replace(".", ",");
};

const simNao = (value?: boolean | "SIM" | "NAO" | null) => {
  if (value === true || value === "SIM") return "Sim";
  if (value === false || value === "NAO") return "Não";
  return "";
};

const asGrade = (value: GradeItem | "" | null | undefined) => {
  if (!value || typeof value !== "object") return undefined;
  return value;
};

const pessoa = (code?: string | number | null, name?: string | null) => {
  const parts = [code, name].filter((item) => item !== undefined && item !== null && String(item).trim() !== "");
  return parts.join(" ");
};

const labelTipo = (value?: string | null) => (value ? value.replace(/_/g, " ") : "");

const Field = ({ label, value }: { label: string; value?: string | number | null }) => (
  <Box component="span" style={{ display: "inline-flex", gap: 4, marginRight: 14, marginBottom: 4, alignItems: "baseline" }}>
    <strong style={{ fontSize: 11 }}>{label}</strong>
    <span style={{ fontSize: 11 }}>{value || ""}</span>
  </Box>
);

const SectionTitle = ({ title }: { title: string }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      margin: "14px 0 8px",
    }}
  >
    <span style={{ flex: 1, borderTop: "1px solid #222" }} />
    <strong style={{ fontSize: 13, whiteSpace: "nowrap" }}>{title}</strong>
    <span style={{ flex: 1, borderTop: "1px solid #222" }} />
  </div>
);

const cellStyle: CSSProperties = {
  border: "1px solid #222",
  padding: "3px 5px",
  fontSize: 11,
  textAlign: "left",
  whiteSpace: "nowrap",
};

export const ContratoCompletoDialog = ({
  open,
  idContract,
  onClose,
}: IContratoCompletoDialog) => {
  const { valueContractInternal } = useContextContrato();
  const { handleGetAlert } = useLayoutMainContext();
  const printRef = useRef<HTMLDivElement>(null);
  const [parcelas, setParcelas] = useState<IListBorderoData[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !idContract) return undefined;

    let active = true;
    setLoading(true);

    api
      .post("/getBordero", {
        id_contrato: idContract,
        page: "1",
        limit: "1000",
        orderByParcelas: "asc",
        isDeleted: "FALSE",
      })
      .then((res) => {
        if (!active) return;
        const rows = (res.data?.data || []) as IListBorderoData[];
        setParcelas(
          [...rows].sort((a, b) => Number(a.numero_parcela) - Number(b.numero_parcela))
        );
      })
      .catch((error) => {
        if (!active) return;
        setParcelas([]);
        handleGetAlert({
          message: error?.response?.data?.message || "Não foi possível carregar as parcelas",
        });
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, idContract]);

  if (!valueContractInternal) return null;

  const contrato = valueContractInternal as ContratoCompletoData;
  const isPF = contrato.tipo_contrato === "PF";
  const supervisorGrade = asGrade(contrato.gradeProdutor?.supervisor);
  const corretorGrade = asGrade(contrato.gradeProdutor?.corretor);
  const supervisorFromParcela = parcelas.find((item) => item.nome_supervisor);

  const documentoCpfCnpj = isPF
    ? (formatString({ type: "cpf", value: contrato.cpf_cnpj || "" }) as string)
    : (formatString({ type: "cnpj", value: contrato.cpf_cnpj || "" }) as string);

  const telefone = contrato.telefone_1
    ? cpfOurCnpj({ text: contrato.telefone_1, type: "phone" })
    : "";

  const handlePrint = () => {
    const node = printRef.current;
    if (!node) return;

    const iframe = document.createElement("iframe");
    iframe.setAttribute("title", "Impressão do contrato");
    iframe.style.position = "fixed";
    iframe.style.left = "-10000px";
    iframe.style.top = "0";
    iframe.style.width = "1200px";
    iframe.style.height = "800px";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    let printed = false;

    const printFrame = () => {
      if (printed) return;
      const frameWindow = iframe.contentWindow;
      if (!frameWindow?.document?.body) return;

      printed = true;
      frameWindow.onafterprint = () => iframe.remove();
      frameWindow.focus();
      frameWindow.print();
      window.setTimeout(() => iframe.remove(), 1500);
    };

    iframe.onload = () => window.setTimeout(printFrame, 50);
    window.setTimeout(printFrame, 400);

    iframe.srcdoc = `<!DOCTYPE html>
      <html>
        <head>
          <title>Contrato completo ${contrato.cod_proposta || ""}</title>
          <style>
            @page { size: A4 landscape; margin: 10mm; }
            body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 12px; }
            table { width: 100%; border-collapse: collapse; }
          </style>
        </head>
        <body>${node.innerHTML}</body>
      </html>`;
  };

  const emitidoEm = new Date().toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        Contrato completo
        <IconButton aria-label="Fechar" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <div ref={printRef} style={{ color: "#111", fontFamily: "Arial, Helvetica, sans-serif" }}>
            <div style={{ display: "flex", justifyContent: "flex-end", fontSize: 11, marginBottom: 4 }}>
              {emitidoEm}
            </div>
            <SectionTitle title="Dados da Proposta" />

            <div>
              {labelTipo(corretorGrade?.tipo_produtor) ? (
                <Field label="Corretor :" value={labelTipo(corretorGrade?.tipo_produtor)} />
              ) : null}
              <Field
                label="Plano"
                value={[contrato.code_modalidades, contrato.name_operadoras, contrato.tipo_contrato]
                  .filter(Boolean)
                  .join(" ")}
              />
              <Field label="Proposta" value={contrato.cod_proposta} />
              <Field
                label="Corretor"
                value={pessoa(contrato.id_produtor, contrato.nome_produtor)}
              />
              <Field
                label="Supervisor"
                value={pessoa(
                  supervisorGrade?.produtor_code || supervisorFromParcela?.id_supervisor,
                  supervisorGrade?.produtor_nome || supervisorFromParcela?.nome_supervisor
                )}
              />
            </div>

            <div style={{ marginTop: 6 }}>
              <Field label="Modalidade" value={contrato.name_modalidades} />
              <Field label="Emissão" value={dateBr(contrato.emissao_data)} />
              <Field label="Assinatura" value={dateBr(contrato.data_assinatura)} />
              <Field label="Vencimento" value={dateBr(contrato.vencimento_data)} />
              <Field label="Vigência" value={dateBr(contrato.vigencia_data)} />
              <Field label="1ª parcela c/ corretor" value={simNao(contrato.is_producer_receveid_installment)} />
              <Field label="Mensalidade" value={money(contrato.mensalidade_value)} />
              <Field label="Vl.Taxa" value={money(contrato.taxa_value)} />
            </div>

            <div style={{ marginTop: 6 }}>
              <Field label="Qtd.Vidas" value={contrato.vidas || "0"} />
              <Field label="Status" value={statusContract[contrato.status]} />
              {contrato.name_administradoras ? (
                <Field label="Administradora" value={contrato.name_administradoras} />
              ) : null}
            </div>

            <div style={{ marginTop: 8, fontSize: 11 }}>
              <strong>Obs.Contrato: </strong>
              <span>{contrato.observacoes || ""}</span>
            </div>

            {isPF ? (
              <>
                <SectionTitle title="Proposta PF" />
                <BlocoPessoaFisica contrato={contrato} documento={documentoCpfCnpj} telefone={telefone} />
              </>
            ) : (
              <>
                <SectionTitle title="Proposta PJ" />
                <BlocoPessoaJuridica contrato={contrato} documento={documentoCpfCnpj} telefone={telefone} />
              </>
            )}

            <SectionTitle title="Dependente(s)" />
            <TabelaDependentes dependentes={contrato.dependentes || []} />

            <SectionTitle title="Borderô de Comissões" />
            <TabelaParcelas parcelas={parcelas} />
          </div>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Fechar</Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={loading}
        >
          Imprimir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const BlocoPessoaFisica = ({
  contrato,
  documento,
  telefone,
}: {
  contrato: ContratoCompletoData;
  documento: string;
  telefone: string;
}) => (
  <div>
    <div>
      <Field label="Email" value={contrato.email} />
      <Field label="Telefone/Celular" value={telefone} />
      <Field label="CPF" value={documento} />
    </div>
    <div>
      <Field label="Titular" value={contrato.titular_pf} />
      <Field label="Nascimento" value={dateBr(contrato.data_nacimento_pf)} />
      <Field label="Sexo" value={contrato.genero_pf} />
    </div>
    <div>
      <Field label="Identidade" value={contrato.rg_pf} />
      <Field label="Estado Civil" value={contrato.estado_civil_pf} />
      <Field label="Responsável" value={contrato.responsavel_pf} />
      <Field label="Mãe" value={contrato.mae_pf} />
    </div>
    <div>
      <Field label="UF" value={contrato.uf} />
      <Field label="Cep" value={handleFormatCep(contrato.cep || "")} />
    </div>
    <div>
      <Field label="Endereço" value={contrato.endereco} />
      <Field label="Número" value={contrato.endereco_numero} />
      <Field label="Compl." value={contrato.complemento} />
      <Field label="Bairro" value={contrato.bairro} />
      <Field label="Cidade" value={contrato.cidade} />
    </div>
  </div>
);

const BlocoPessoaJuridica = ({
  contrato,
  documento,
  telefone,
}: {
  contrato: ContratoCompletoData;
  documento: string;
  telefone: string;
}) => (
  <div>
    <div>
      <Field label="Razão Social" value={contrato.razao_social_pj} />
      <Field label="Contato / Responsável" value={contrato.contato_responsavel_pj} />
      <Field label="Cargo" value={contrato.cargo_pj} />
    </div>
    <div>
      <Field label="Telefone" value={telefone} />
      <Field label="Email" value={contrato.email} />
      <Field label="CNPJ" value={documento} />
    </div>
    <div>
      <Field label="Cep" value={handleFormatCep(contrato.cep || "")} />
      <Field label="Endereço" value={contrato.endereco} />
      <Field label="Número" value={contrato.endereco_numero} />
      <Field label="Complemento" value={contrato.complemento} />
      <Field label="Bairro" value={contrato.bairro} />
      <Field label="Cidade" value={contrato.cidade} />
      <Field label="UF" value={contrato.uf} />
    </div>
  </div>
);

const TabelaDependentes = ({ dependentes }: { dependentes: IDependentesContrato[] }) => (
  <table style={{ width: "100%", borderCollapse: "collapse" }}>
    <thead>
      <tr>
        {["Dependente(s)", "Nascimento", "Sexo", "Dependente (S/N)", "CPF"].map((header) => (
          <th key={header} style={{ ...cellStyle, fontWeight: 700 }}>
            {header}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {dependentes.length === 0 ? (
        <tr>
          <td colSpan={5} style={cellStyle}>
            Nenhum dependente
          </td>
        </tr>
      ) : (
        dependentes.map((item) => (
          <tr key={item.id || `${item.nome}-${item.cpf}`}>
            <td style={cellStyle}>{item.nome}</td>
            <td style={cellStyle}>{dateBr(item.data_nascimento)}</td>
            <td style={cellStyle}>{item.sexo}</td>
            <td style={cellStyle}>S</td>
            <td style={cellStyle}>
              {item.cpf ? (formatString({ type: "cpf", value: item.cpf }) as string) : ""}
            </td>
          </tr>
        ))
      )}
    </tbody>
  </table>
);

const TabelaParcelas = ({ parcelas }: { parcelas: IListBorderoData[] }) => (
  <div style={{ overflowX: "auto" }}>
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          {["Parcela", "Data Pagamento", "Valor Pago", "% Comissão", "Valor Comissão", "Vencimento", "Previsão"].map(
            (header) => (
              <th key={header} style={{ ...cellStyle, fontWeight: 700 }}>
                {header}
              </th>
            )
          )}
        </tr>
      </thead>
      <tbody>
        {parcelas.length === 0 ? (
          <tr>
            <td colSpan={7} style={cellStyle}>
              Nenhuma parcela
            </td>
          </tr>
        ) : (
          parcelas.map((item) => (
            <tr key={item.id}>
              <td style={cellStyle}>{String(item.numero_parcela).padStart(2, "0")}</td>
              <td style={cellStyle}>{item.is_parcela_recebida ? dateBr(item.data_recebimento) : ""}</td>
              <td style={cellStyle}>{money(item.is_parcela_recebida ? item.valor_recebido : "0")}</td>
              <td style={cellStyle}>{percent(item.porcentagem_comissao_corretora)}</td>
              <td style={cellStyle}>{money(item.valor_comissao_corretora)}</td>
              <td style={cellStyle}>{dateBr(item.data_vencimento_parcela)}</td>
              <td style={cellStyle}>{dateBr(item.data_previsao)}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);
